import { Link, useNavigate } from 'react-router';
import { useMemo } from 'react';
import { DashboardCardLinkButton } from '../storageDashboard/DashboardCardLinkButton';
import { showErrorToast } from '../notifications/toast';
import { MyAppsIcon } from './MyAppsIcon';
import { BROWSE_ALL_APPS_PATH, buildMyAppsShortcutLeaves } from './myAppsNav';
import { launchMyAppsEntry } from './myAppsSearch';
import type { MyAppsRegistryEntry } from './myAppsTypes';
import { useMyAppsEntries } from './useMyAppsEntries';
import { useMyAppsStore } from './useMyAppsStore';
import './myApps.css';

export const MY_APPS_SHORTCUTS_CARD_ID = 'my-apps-shortcuts';

export interface MyAppsShortcutsCardProps {
  registry?: readonly MyAppsRegistryEntry[];
}

export function MyAppsShortcutsCard({ registry }: MyAppsShortcutsCardProps = {}) {
  const navigate = useNavigate();
  const { status, error, entriesById, navEntriesById, customAppIds, pinnedToolIds, reload } =
    useMyAppsEntries(registry);
  const recordRecent = useMyAppsStore((s) => s.recordRecent);

  const shortcuts = useMemo(
    () => buildMyAppsShortcutLeaves(pinnedToolIds, navEntriesById, customAppIds),
    [customAppIds, navEntriesById, pinnedToolIds]
  );

  return (
    <section
      className="dashboard-card dashboard-card--storage my-apps-shortcuts-card"
      aria-label="My Apps"
    >
      <div className="dashboard-card__header">
        <div>
          <h3 className="dashboard-card__title">My Apps</h3>
          <p>Pinned and custom apps</p>
        </div>
        <DashboardCardLinkButton text="Browse apps" href={BROWSE_ALL_APPS_PATH} />
      </div>

      <div className="dashboard-kpi-row">
        <div className="dashboard-kpi">
          <span className="dashboard-kpi__value">
            {status === 'ready' ? pinnedToolIds.length : '—'}
          </span>
          <span className="dashboard-kpi__label">Pinned</span>
        </div>
        <div className="dashboard-kpi">
          <span className="dashboard-kpi__value">
            {status === 'ready' ? entriesById.size : '—'}
          </span>
          <span className="dashboard-kpi__label">Available to you</span>
        </div>
      </div>

      <div className="dashboard-card__section my-apps-shortcuts-card__section">
        <h4 className="dashboard-card__section-title">Shortcuts</h4>
        {status === 'loading' || status === 'idle' ? (
          <p className="dashboard-empty" role="status">
            Loading shortcuts…
          </p>
        ) : null}

        {status === 'error' ? (
          <div className="dashboard-empty">
            <p>{error ?? 'Could not load pinned apps.'}</p>
            <button
              type="button"
              className="my-apps-shortcuts-card__retry"
              onClick={() => {
                void reload().catch((err) =>
                  showErrorToast(err instanceof Error ? err.message : 'Could not load My Apps.')
                );
              }}
            >
              Retry
            </button>
          </div>
        ) : null}

        {status === 'ready' && shortcuts.length === 0 ? (
          <p className="dashboard-empty">
            Pin apps you use often from <Link to={BROWSE_ALL_APPS_PATH}>Browse All Apps</Link>.
          </p>
        ) : null}

        {status === 'ready' && shortcuts.length > 0 ? (
          <ul className="my-apps-shortcuts-card__grid" aria-label="My Apps shortcuts">
            {shortcuts.map((entry) => {
              const app = entriesById.get(entry.id);
              return (
                <li key={entry.id}>
                  <button
                    type="button"
                    className="my-apps-shortcuts-card__tile"
                    title={entry.text}
                    onClick={() => {
                      const opened = launchMyAppsEntry(
                        {
                          id: entry.id,
                          kind: app?.kind ?? 'app',
                          path: entry.path,
                          url: entry.url ?? app?.url
                        },
                        navigate
                      );
                      if (!opened) {
                        showErrorToast('Could not open this app.');
                        return;
                      }
                      void recordRecent(entry.id).catch(() => undefined);
                    }}
                  >
                    <MyAppsIcon
                      icon={app?.icon ?? ''}
                      className="my-apps-shortcuts-card__tile-icon"
                    />
                    <span>{entry.text}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </section>
  );
}
